import type { PermissionFeature } from '@/utils/permissions';

export const VISIT_TYPES = [
  { value: 'longevity', label: 'Longevity' },
  { value: 'in_app_early', label: 'In-App Early' },
  { value: 'walk_in', label: 'Doctor Consultation' },
  { value: 'in_centre_visit', label: 'In Centre Visit' },
  { value: 'prescription', label: 'Prescription Refill' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'health_coach', label: 'Health Coach' },
  { value: 'review_consultation', label: 'Review' },
  { value: 'adhoc_consultation', label: 'Ad Hoc Consultation' },
  { value: 'hbot', label: 'HBOT' },
  { value: 'cryo', label: 'Cryo Therapy' },
  { value: 'red_light', label: 'Red Light' },
  { value: 'fir', label: 'FIR Therapy' },
  { value: 'iv', label: 'IV Therapy' },
  { value: 'pet', label: 'Physical Therapy' },
  { value: 'psychotherapy', label: 'Psychotherapy' },
];

/** Tab ids whose public URL param differs from the stored id. */
const CLIENT_DETAILS_TAB_URL_PARAMS: Record<string, string> = {
  journal: 'nutrition',
};

const CLIENT_DETAILS_URL_PARAM_TO_TAB: Record<string, string> = Object.fromEntries(
  Object.entries(CLIENT_DETAILS_TAB_URL_PARAMS).map(([tabId, param]) => [param, tabId])
);

export const clientDetailsTabToUrlParam = (tabId: string): string =>
  CLIENT_DETAILS_TAB_URL_PARAMS[tabId] ?? tabId;

export const clientDetailsUrlParamToTab = (param: string | null | undefined): string | null => {
  if (!param) return null;
  return CLIENT_DETAILS_URL_PARAM_TO_TAB[param] ?? param;
};

export const TAB_LABELS: Record<string, string> = {
  prep: 'Prep',
  visits: 'Visits',
  journey: 'Journey',
  appointments: 'Appointments',
  consultations: 'Consultations',
  clinical: 'Clinical',
  internal_notes: 'Internal Notes',
  clinical_notes: 'Clinical Notes',
  care_team: 'Care Team',
  journal: 'Nutrition',
  offerings: 'Memberships',
  addons: 'Add-ons',
  test_enrollments: 'Tests',
  marketplace: 'Marketplace',
  purchases: 'Purchases',
  memberships_overview: 'Memberships Overview',
  questionnaires: 'Questionnaires',
  biomarker_analysis: 'Biomarker Analysis',
  reports: 'Reports',
  fitness_assessment: 'Fitness Assessment',
  videos: 'Videos',
  pmx_reports: 'PMX Reports',
  thrive_intelligence: 'Thrive Intelligence',
  genetic_features: 'Genome Report',
  roadmap: 'Roadmap',
  sift: 'SIFT',
  wearables: 'Wearables',
  wearable_analysis: 'Wearable Analysis',
  daily_log: 'Daily Log',
  chat: 'Chat',
  context: 'Context',
  reminders: 'Reminders',
  workbench: 'Workbench',
};

/** Tabs that sit on the client-details bar first. Rearrange still wins per browser. */
export const PRIMARY_CLIENT_DETAILS_TABS = [
  'visits',
  'journey',
  'consultations',
  'context',
  'prep',
  'biomarker_analysis',
  'reports',
  'offerings',
  'test_enrollments',
  'clinical_notes',
  'internal_notes',
  'journal',
  'daily_log',
] as const;

export const SECONDARY_CLIENT_DETAILS_TABS = [
  'appointments',
  'clinical',
  'addons',
  'questionnaires',
  'marketplace',
  'purchases',
  'memberships_overview',
  'videos',
  'pmx_reports',
  'fitness_assessment',
  'thrive_intelligence',
  'genetic_features',
  'roadmap',
  'wearables',
  'wearable_analysis',
  'sift',
  'care_team',
  'chat',
  'reminders',
  'workbench',
] as const;

export const BASE_TABS: string[] = [
  ...PRIMARY_CLIENT_DETAILS_TABS,
  ...SECONDARY_CLIENT_DETAILS_TABS,
];

/**
 * Maps client-details tabs → backend matrix feature/action.
 *
 * A tab with no entry stays visible on purpose. Those tabs are legacy
 * surfaces gated by view settings, not by a matrix feature. A new tab that
 * calls an API must be added to TAB_LIST_PERMISSIONS or it will show for
 * every role.
 *
 * `list` also accepts `read`, because some roles are granted read without a
 * separate list action and the tab only needs to open one client's record.
 */
type TabPermissionRequirement = {
  feature: PermissionFeature;
  action: 'list' | 'read' | 'create' | 'update' | 'delete';
  anyOf?: Array<Omit<TabPermissionRequirement, 'anyOf'>>;
};

const tabPermissionGranted = (
  requirement: Omit<TabPermissionRequirement, 'anyOf'>,
  hasPermission: (feature: PermissionFeature, action: TabPermissionRequirement['action']) => boolean
): boolean => {
  if (hasPermission(requirement.feature, requirement.action)) return true;
  if (requirement.action === 'list' && hasPermission(requirement.feature, 'read')) return true;
  return false;
};

/** True when this client-details tab's backing API is listable for the user. */
export const canAccessClientDetailsTab = (
  tabId: string,
  hasPermission: (feature: PermissionFeature, action: TabPermissionRequirement['action']) => boolean
): boolean => {
  const requirement = TAB_LIST_PERMISSIONS[tabId];
  if (!requirement) return true;
  if (tabPermissionGranted(requirement, hasPermission)) return true;
  return (requirement.anyOf ?? []).some((alternate) =>
    tabPermissionGranted(alternate, hasPermission)
  );
};

export const TAB_LIST_PERMISSIONS: Partial<Record<string, TabPermissionRequirement>> = {
  prep: { feature: 'client_prep', action: 'list' },
  visits: { feature: 'user_visits', action: 'list' },
  journey: { feature: 'journey_activity', action: 'list' },
  appointments: { feature: 'appointments', action: 'list' },
  consultations: { feature: 'consultations', action: 'list' },
  clinical: { feature: 'references_signs', action: 'list' },
  internal_notes: { feature: 'internal_notes', action: 'list' },
  clinical_notes: { feature: 'clinical_notes', action: 'list' },
  care_team: { feature: 'doctor_assignment', action: 'list' },
  journal: {
    feature: 'user_journal',
    action: 'list',
    anyOf: [{ feature: 'nutrition_meal_plans', action: 'list' }],
  },
  offerings: { feature: 'user_program_enrollments', action: 'list' },
  addons: { feature: 'user_addon_enrollments', action: 'list' },
  test_enrollments: { feature: 'user_partner_test_enrollments', action: 'list' },
  marketplace: { feature: 'marketplace_catalog', action: 'list' },
  purchases: { feature: 'marketplace_catalog', action: 'list' },
  memberships_overview: { feature: 'marketplace_catalog', action: 'list' },
  questionnaires: { feature: 'assessment_responses', action: 'list' },
  biomarker_analysis: { feature: 'user_biomarker_analyses', action: 'list' },
  reports: { feature: 'external_reports', action: 'list' },
  fitness_assessment: { feature: 'fitness_assessments', action: 'list' },
  videos: { feature: 'consultation_videos', action: 'list' },
  pmx_reports: { feature: 'internal_reports', action: 'list' },
  thrive_intelligence: { feature: 'thrive_iq_executions', action: 'list' },
  genetic_features: { feature: 'genetic_profiles', action: 'list' },
  roadmap: { feature: 'user_program_enrollments', action: 'list' },
  sift: { feature: 'user_biomarker_analyses', action: 'list' },
  wearables: { feature: 'wearable_connections', action: 'list' },
  wearable_analysis: { feature: 'wearable_connections', action: 'list' },
  daily_log: { feature: 'daily_intake_logs', action: 'list' },
  chat: { feature: 'platform_chat', action: 'read' },
  context: { feature: 'platform_chat', action: 'read' },
  reminders: { feature: 'user_reminder_management', action: 'list' },
  workbench: { feature: 'workbench_contexts', action: 'list' },
};
/** Bump to ship a new factory order to browsers still on an older default. */
export const CLIENT_DETAILS_TAB_ORDER_VERSION = 4;

export function resolveClientDetailsTabOrder(
  savedOrder: unknown,
  savedVersion: unknown,
  defaultOrder: string[] = BASE_TABS
): string[] {
  const existing = Array.isArray(savedOrder)
    ? savedOrder.filter((id): id is string => typeof id === 'string')
    : [];
  const version = typeof savedVersion === 'number' ? savedVersion : 0;
  if (!existing.length) return [...defaultOrder];
  if (version < CLIENT_DETAILS_TAB_ORDER_VERSION) {
    const known = new Set(defaultOrder);
    return [...defaultOrder, ...existing.filter((id) => !known.has(id))];
  }
  const next = [...existing];
  defaultOrder.forEach((tab, defaultIdx) => {
    if (next.includes(tab)) return;
    let insertAt = next.length;
    for (let i = defaultIdx - 1; i >= 0; i -= 1) {
      const anchorIdx = next.indexOf(defaultOrder[i]);
      if (anchorIdx !== -1) {
        insertAt = anchorIdx + 1;
        break;
      }
    }
    next.splice(insertAt, 0, tab);
  });
  return next;
}

export const CONFIRMATION_TEXTS = {
  DELETE_VISIT: 'delete visit',
  DELETE_CONSULTATION: 'delete consultation',
  DELETE_PROGRAM: 'delete program',
  DELETE_ADDON: 'delete addon',
  DELETE_TEST: 'delete test',
};

export const TAB_GRID_GROUPS = [
  {
    title: 'Care timeline',
    description: 'Visits, scheduling, follow-ups, and care coordination.',
    tabs: [
      'prep',
      'visits',
      'journey',
      'appointments',
      'consultations',
      'context',
      'roadmap',
      'reminders',
    ],
  },
  {
    title: 'Clinical record',
    description: 'Provider notes, clinical findings, biomarkers, and health data.',
    tabs: [
      'clinical',
      'internal_notes',
      'clinical_notes',
      'biomarker_analysis',
      'genetic_features',
      'wearables',
      'wearable_analysis',
      'sift',
    ],
  },
  {
    title: 'Reports and intelligence',
    description: 'Generated reports, questionnaires, and Thrive analysis.',
    tabs: [
      'reports',
      'fitness_assessment',
      'pmx_reports',
      'questionnaires',
      'thrive_intelligence',
      'workbench',
    ],
  },
  {
    title: 'Nutrition and lifestyle',
    description: 'Nutrition journal and behavior context.',
    tabs: ['journal', 'daily_log'],
  },
  {
    title: 'Memberships and services',
    description: 'Programs, add-ons, tests, marketplace, purchases, and videos.',
    tabs: [
      'offerings',
      'addons',
      'test_enrollments',
      'marketplace',
      'purchases',
      'memberships_overview',
      'videos',
    ],
  },
  {
    title: 'Communication',
    description: 'Care team and client chat.',
    tabs: ['care_team', 'chat'],
  },
] as const;

export const TAB_GRID_DESCRIPTIONS: Record<string, string> = {
  prep: 'Pre-consultation prep — synthesised profile, biomarkers, Thrive Check, reports, and visit history in one view.',
  visits: 'Membership-linked visits and visit-level actions.',
  journey:
    'Member journey timeline — purchases, appointments, doctor interactions, reports, and onboarding.',
  appointments: 'Upcoming and past scheduled appointments.',
  consultations: 'Consultation records across visit types.',
  context: 'Visit and consultation context for this member.',
  roadmap: 'Longitudinal care path and report milestones.',
  reminders: 'Client reminders and follow-up nudges.',
  clinical: 'Signs, symptoms, diagnoses, advice, and medications.',
  internal_notes: 'Staff-only notes for internal coordination.',
  clinical_notes: 'Patient-visible clinical communication.',
  biomarker_analysis: 'Biomarker interpretation and trend context.',
  genetic_features: 'Genome-driven risk and drug safety insights.',
  wearables: 'Connected wearable data and daily signals.',
  wearable_analysis: 'Provider-facing wearable analysis views.',
  daily_log: 'Meal, symptom, and daily intake logs.',
  sift: 'Focused filtering and intelligence workflow.',
  reports: 'External reports and uploaded result documents.',
  fitness_assessment:
    'Enter fitness test scores, save them for this client, and upload to Reports.',
  pmx_reports: 'Internal PMX report outputs.',
  questionnaires: 'Assessment responses and forms.',
  thrive_intelligence: 'Thrive analysis, protocols, and recommendations.',
  journal: 'Nutrition entries and client lifestyle context.',
  offerings: 'Memberships, programs, and enrollment status.',
  addons: 'Purchased or assigned add-on services.',
  test_enrollments: 'Lab tests, orders, and partner test enrollment.',
  marketplace: 'Available client-facing products and services.',
  purchases: 'Purchase history and entitlement context.',
  videos: 'Assigned videos and education content.',
  care_team: 'Assigned providers and support ownership.',
  chat: 'Client conversation and AI-assisted context.',
  workbench: 'Load this client\u2019s reports and health data, then ask questions about them.',
};
